import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { previewFeaturesAPI } from "../../apis"
import { addErrorNotification } from "../notifications/actions"
import { PayloadActionType } from "../../store"
import { IFooter, IPreviewFeature } from "./index"

export const defaultUpdatePreviewErrorMessage =
    "Something went wrong setting preview features "

export const fetchPreviewFeatures = createAsyncThunk(
    "fetchPreviewFeatures",
    async () => {
        const [
            { previewFeatures: enabledPreviewFeatures },
            { previewFeatures: betaPreviewFeatures },
        ] = await Promise.all([
            previewFeaturesAPI.getEnabledPreviewFeatures(),
            previewFeaturesAPI.getBetaPreviewFeatures(),
        ])
        return betaPreviewFeatures.map((previewFeature) => ({
            ...previewFeature,
            isEnabled: !!enabledPreviewFeatures.find(
                (_previewFeature) => _previewFeature.id === previewFeature.id,
            ),
        }))
    },
)

export const updatePreviewFeatureDisplay = createAsyncThunk(
    "updatePreviewFeatureDisplay",
    async (
        {
            features,
            currentPreviewFeatures,
        }: {
            currentPreviewFeatures: Map<number, boolean>
            features: IPreviewFeature[]
        },
        { dispatch },
    ) => {
        const promises: Promise<PayloadActionType<unknown>>[] = []

        currentPreviewFeatures.forEach((value, key) => {
            if (features?.some((f) => f.id === key && f.isEnabled !== value)) {
                const action = value
                    ? enablePreviewFeatureForUser(key)
                    : disablePreviewFeatureForUser(key)
                promises.push(dispatch(action))
            }
        })
        const results = await Promise.allSettled(promises)

        const atLeastOneFulfilled = results.some((result) => {
            if (
                result.status === "fulfilled" &&
                (result.value as PayloadActionType<number>).meta
                    .requestStatus === "fulfilled"
            ) {
                return true
            } else if (result.status === "fulfilled") {
                console.error(
                    "An error occurred setting preview feature:",
                    result.value.payload,
                )
            } else {
                console.error(
                    "An error occurred setting preview feature:",
                    result.reason,
                )
                dispatch(addErrorNotification(defaultUpdatePreviewErrorMessage))
            }
            return false
        })
        return atLeastOneFulfilled
    },
)

export const enablePreviewFeatureForUser = createAsyncThunk(
    "enablePreviewFeatureForUser",
    async (previewFeatureId: number, { rejectWithValue, dispatch }) => {
        try {
            await previewFeaturesAPI.enablePreviewFeatureForUser(
                previewFeatureId,
            )
            return previewFeatureId
        } catch (error) {
            dispatch(addErrorNotification(await buildErrorMessage(error)))
            return rejectWithValue(error || defaultUpdatePreviewErrorMessage)
        }
    },
)

export const disablePreviewFeatureForUser = createAsyncThunk(
    "disablePreviewFeatureForUser",
    async (previewFeatureId: number, { rejectWithValue, dispatch }) => {
        try {
            await previewFeaturesAPI.disablePreviewFeatureForUser(
                previewFeatureId,
            )
            return previewFeatureId
        } catch (error) {
            dispatch(addErrorNotification(await buildErrorMessage(error)))
            return rejectWithValue(error || defaultUpdatePreviewErrorMessage)
        }
    },
)

const buildErrorMessage = async (error: unknown): Promise<string> => {
    let errorMessage = `${defaultUpdatePreviewErrorMessage}`
    if (error instanceof Response) {
        let body = undefined
        try {
            // Try catch here because this could respond with html instead of json
            body = await error.json()
        } catch (jsonError) {
            console.warn(jsonError)
        }
        if (body?.detail) {
            errorMessage = `${errorMessage}. ${body.detail}`
        } else if (body?.error?.message) {
            errorMessage = `${body.error.message}`
        } else if (error.statusText) {
            errorMessage = `${errorMessage}: ${error.statusText}`
        }
    }
    return errorMessage
}

export const footerSlice = createSlice({
    name: "footer",

    initialState: {
        isLoading: false,
        previewFeatures: [],
    } as IFooter,

    reducers: {},

    extraReducers: (builder) => {
        builder
            .addCase(fetchPreviewFeatures.pending, (state) => {
                state.isLoading = true
            })
            .addCase(fetchPreviewFeatures.rejected, (state) => {
                state.isLoading = false
            })
            .addCase(fetchPreviewFeatures.fulfilled, (state, action) => {
                state.previewFeatures = action.payload
                state.isLoading = false
            })
            .addCase(enablePreviewFeatureForUser.pending, (state) => {
                state.isLoading = true
            })
            .addCase(enablePreviewFeatureForUser.rejected, (state) => {
                state.isLoading = false
            })
            .addCase(enablePreviewFeatureForUser.fulfilled, (state, action) => {
                const target = state.previewFeatures.find(
                    (previewFeature) => previewFeature.id === action.payload,
                )
                if (target) {
                    target.isEnabled = true
                }
                state.isLoading = false
            })
            .addCase(disablePreviewFeatureForUser.pending, (state) => {
                state.isLoading = true
            })
            .addCase(disablePreviewFeatureForUser.rejected, (state) => {
                state.isLoading = false
            })
            .addCase(
                disablePreviewFeatureForUser.fulfilled,
                (state, action) => {
                    const target = state.previewFeatures.find(
                        (previewFeature) =>
                            previewFeature.id === action.payload,
                    )
                    if (target) {
                        target.isEnabled = false
                    }
                    state.isLoading = false
                },
            )
            .addCase(updatePreviewFeatureDisplay.pending, (state) => {
                state.isLoading = true
            })
            .addCase(updatePreviewFeatureDisplay.rejected, (state) => {
                state.isLoading = false
            })
            .addCase(updatePreviewFeatureDisplay.fulfilled, (state, action) => {
                state.isLoading = false
                if (action.payload) {
                    window.location.reload()
                }
            })
    },
})

export default footerSlice.reducer
